<template>
  <x-form
    :disabled="!login.grants.developer"
    submit-text=""
    @update:sending="saving = $event"
    @submit="save"
  >
    <pf-empty-state v-if="loading">
      <pf-empty-state-header :title="_t('Caricamento configurazione zona in corso...')" heading-level="h4">
        <template #icon>
          <pf-empty-state-icon>
            <pf-spinner />
          </pf-empty-state-icon>
        </template>
      </pf-empty-state-header>
    </pf-empty-state>

    <pf-table v-else compact striped>
      <pf-thead>
        <pf-tr>
          <pf-th>{{ $t('Dominio') }}</pf-th>
          <pf-th>TTL</pf-th>
          <pf-th>
            {{ $t('Tipo') }}
          </pf-th>
          <pf-th>
            {{ $t('Peso') }}
          </pf-th>
          <pf-th>Target</pf-th>
          <pf-th v-if="login.grants.developer" :screen-reader-text="$t('Azioni')" />
        </pf-tr>
      </pf-thead>
      <pf-tbody>
        <pf-tr v-for="(record, i) in records" :key="i" role="row">
          <pf-td :data-label="$t('Dominio')">
            <x-input
              v-model="record.domain"
              :placeholder="$t('Dominio')"
              :disabled="!login.grants.developer"
              :pattern="domainValidPattern(record)"
              :title="record.type === 'CNAME' ? $t('Non è possibile mischiare record CNAME con altri tipi per lo stesso dominio') : undefined"
              required
            />
          </pf-td>
          <pf-td data-label="TTL" class="ttl">
            <x-input
              :model-value="record.ttl"
              @update:model-value="record.ttl = $event"
              type="number"
              placeholder="TTL"
              min="800"
              max="2147483647"
              :disabled="!login.grants.developer"
            />
          </pf-td>
          <pf-td :data-label="$t('Tipo')" class="type">
            <pf-form-select :aria-label="$t('Tipo')" :model-value="record.type" @update:model-value="record.type = String($event)" :disabled="!login.grants.developer">
              <pf-form-select-option value="A">A</pf-form-select-option>
              <pf-form-select-option value="AAAA">AAAA</pf-form-select-option>
              <pf-form-select-option value="CAA">CAA</pf-form-select-option>
              <pf-form-select-option value="CNAME">CNAME</pf-form-select-option>
              <pf-form-select-option value="DNAME">DNAME</pf-form-select-option>
              <pf-form-select-option value="DS">DS</pf-form-select-option>
              <pf-form-select-option value="LOC">LOC</pf-form-select-option>
              <pf-form-select-option value="MX">MX</pf-form-select-option>
              <pf-form-select-option value="TXT">TXT</pf-form-select-option>
              <pf-form-select-option value="RP">RP</pf-form-select-option>
              <pf-form-select-option value="SRV">SRV</pf-form-select-option>
            </pf-form-select>
          </pf-td>
          <pf-td :data-label="$t('Peso')" class="weight">
            <x-input
              v-if="record.type == 'MX'"
              v-model="record.weight"
              type="number"
              :placeholder="$t('Peso')"
              min="0"
              max="2147483647"
            />
          </pf-td>
          <pf-td data-label="Target" >
            <x-input v-model="record.target" :type="targetType(record.domain, record.type)" placeholder="Target" :disabled="!login.grants.developer" required>
              <template #append v-if="dominio && ['CNAME', 'DNAME', 'MX'].includes(record.type ?? '') && !isFQ(record.target)">
                .{{ dominio }}
              </template>
            </x-input>
          </pf-td>
          <pf-td v-if="login.grants.developer" class="actions">
            <pf-button variant="danger" v-bind="{title: 'Elimina'}" @click="remove(i)">
              <template #icon><trash-icon /></template>
            </pf-button>
          </pf-td>
        </pf-tr>
      </pf-tbody>
    </pf-table>

    <template #actions>
      <template v-if="login.grants.developer">
        <pf-button type="submit" variant="primary" :disabled="!modified || saving" :loading="saving">
          <template #icon><save-icon /></template>
          {{ $t('Salva') }}
        </pf-button>

        <pf-button :disabled="!modified" variant="link" @click="undo">
          {{ $t('Annulla') }}
        </pf-button>
      </template>

      <pf-helper-text v-else>
        <pf-helper-text-item variant="warning">
          {{ $t('Modifica dei record DNS non abilitata. Richiedi i permessi tecnici ad un utente amministratore per abilitarla.') }}
        </pf-helper-text-item>
      </pf-helper-text>

      <pf-flex-item style="flex-grow: 1" />

      <template v-if="login.grants.developer">
        <pf-dropdown :text="$t('Record di posta predefiniti')">
          <pf-dropdown-item v-if="dominio" @click="addOffice365Records">
            {{ $t('Aggiungi record di Office 365') }}
          </pf-dropdown-item>
          <pf-dropdown-item @click="addGoogleMxRecords">
            {{ $t('Aggiungi record di GMail') }}
          </pf-dropdown-item>
        </pf-dropdown>

        <pf-button variant="control" :disabled="loading" @click="add">
          <template #icon><circle-plus-icon /></template>
          {{ $t('Nuovo Record') }}
        </pf-button>
      </template>

      <pf-button variant="control" :disabled="loading" @click="scarica">
        <template #icon><download-icon /></template>
        {{ $t('Scarica la zona DNS') }}
      </pf-button>
    </template>
  </x-form>
</template>

<style scoped>
.pf-v6-c-table__td.ttl,
.pf-v6-c-table__td.type,
.pf-v6-c-table__td.weight {
  --pf-v6-c-table--cell--MaxWidth: 5em;
}

.pf-v6-c-table__td.actions {
  --pf-v6-c-table--cell--MaxWidth: 3.5em;
}
</style>

<script setup lang="ts">
import isEqual from 'lodash-es/isEqual';
import cloneDeep from 'lodash-es/cloneDeep';
import { saveAs } from '@common/utils';
import { DnsResource, type DnsRecord } from '@/resources';
import { useLoginStore } from '@/store/login';
import { computed, watch, type Ref, ref } from 'vue';
import { isDefined } from '@vueuse/shared';
import { $t as _t } from '@/i18n';
import escapeStringRegexp from 'escape-string-regexp';
import DownloadIcon from '@vue-patternfly/icons/download-icon';
import CirclePlusIcon from '@vue-patternfly/icons/circle-plus-icon';
import SaveIcon from '@vue-patternfly/icons/save-alt-icon';
import TrashIcon from '@vue-patternfly/icons/trash-icon';
import { useFunctionRunningState } from '@common/use';

defineOptions({
  name: 'DnsRecords',
});

const props = defineProps<{
  dominio: string;
}>();

const login = useLoginStore();
const records: Ref<Partial<DnsRecord>[]> = ref([]);
const undoRecords: Ref<Partial<DnsRecord>[]> = ref([]);

const modified = computed(() => !isEqual(records.value, undoRecords.value));
const saving = ref(false);

const { isRunning: loading, run: onDomainChange } = useFunctionRunningState(async() => {
  records.value = await new DnsResource().get(props.dominio);
  undoRecords.value = cloneDeep(records.value);
});

watch(() => props.dominio, onDomainChange, { immediate: true });

function add() {
  records.value.push({
    type: 'A',
  });
}

function remove(record: number) {
  records.value.splice(record, 1);
}

function undo() {
  records.value = cloneDeep(undoRecords.value);
}

async function save() {
  await new DnsResource().save(props.dominio, records.value);
  undoRecords.value = cloneDeep(records.value);
}

function isDKIM(domain: string | null | undefined) {
  return domain?.substring(domain.length - 11) === '._domainkey';
}

function isDMARC(domain: string | null | undefined) {
  return domain === '_dmarc' || domain?.startsWith('_dmarc.');
}

function isFQ(target: string | null | undefined) {
  return target?.substring(target.length - 1) === '.';
}

function targetType(domain: string | null | undefined, type: string | null | undefined) {
  if (type == 'TXT' && isDKIM(domain)) {
    return 'dkim';
  }
  if (type == 'TXT' && isDMARC(domain)) {
    return 'dmarc';
  }
  if (type == 'A') {
    return 'ipv4';
  }
  return 'text';
}

function domainValidPattern(record: Partial<DnsRecord>) {
  if (record.type !== 'CNAME') {
    return undefined;
  }
  const otherDomains = records.value
    .filter(r => r.domain && r !== record)
    .map(r => r.domain)
    .filter((d, index, array) => array.indexOf(d) === index);
  if (!otherDomains.length) {
    return undefined;
  }
  return `(?!${otherDomains.map(d => `${escapeStringRegexp(d ?? '')}$`).join('|')}).*`;
}

function removeMatchingRecords(f: (record: Partial<DnsRecord>) => boolean) {
  for (let i = records.value.length - 1; i >= 0; --i) {
    const r = records.value[i];
    if (r && f(r)) {
      remove(i);
    }
  }
}

function removeAllMxRecords() {
  removeMatchingRecords(r => r.type === 'MX');
}

function findSpfRecord() {
  for (const record of records.value) {
    if (record.type === 'TXT' && record.target?.startsWith('v=spf1 ')) {
      return record;
    }
  }
}

function pushRecord(record: Partial<DnsRecord>) {
  if (record.type === 'CNAME') {
    removeMatchingRecords(r => record.domain === r.domain);
  }
  records.value.push(record);
}

function addGoogleMxRecords() {
  if (!window.confirm(_t('Attenzione! Eventuali MX già presenti verranno rimossi e sostituiti da quelli scelti. Vuoi procedere?'))) {
    return;
  }

  removeAllMxRecords();

  const spf = findSpfRecord();
  if (spf?.target) {
    if (!spf.target.includes(' include:_spf.google.com ')) {
      spf.target = spf.target.replace('v=spf1 ', 'v=spf1 include:_spf.google.com ');
    }
  } else {
    pushRecord({
      domain: '@',
      type: 'TXT',
      target: 'v=spf1 a mx include:spf.artera.net include:_spf.google.com -all',
    });
  }

  pushRecord({
    domain: '@',
    type: 'MX',
    weight: 1,
    target: 'ASPMX.L.GOOGLE.COM.',
  });
  pushRecord({
    domain: '@',
    type: 'MX',
    weight: 5,
    target: 'ALT1.ASPMX.L.GOOGLE.COM.',
  });
  pushRecord({
    domain: '@',
    type: 'MX',
    weight: 5,
    target: 'ALT2.ASPMX.L.GOOGLE.COM.',
  });
  pushRecord({
    domain: '@',
    type: 'MX',
    weight: 10,
    target: 'ALT3.ASPMX.L.GOOGLE.COM.',
  });
  pushRecord({
    domain: '@',
    type: 'MX',
    weight: 10,
    target: 'ALT4.ASPMX.L.GOOGLE.COM.',
  });
}

function addOffice365Records() {
  if (!props.dominio) {
    return;
  }

  if (!window.confirm(_t('Attenzione! Eventuali MX già presenti verranno rimossi e sostituiti da quelli scelti. Vuoi procedere?'))) {
    return;
  }

  removeAllMxRecords();

  const spf = findSpfRecord();
  if (spf?.target) {
    if (!spf.target.includes(' include:spf.protection.outlook.com ')) {
      spf.target = spf.target.replace('v=spf1 ', 'v=spf1 include:spf.protection.outlook.com ');
    }
  } else {
    pushRecord({
      domain: '@',
      type: 'TXT',
      target: 'v=spf1 a mx include:spf.artera.net include:spf.protection.outlook.com -all',
    });
  }

  pushRecord({
    domain: '@',
    type: 'MX',
    weight: 0,
    target: `${props.dominio.replace(/\./g, '-')}.mail.protection.outlook.com.`,
  });
  pushRecord({
    domain: 'autodiscover',
    type: 'CNAME',
    ttl: 3600,
    target: 'autodiscover.outlook.com.',
  });
  pushRecord({
    domain: 'sip',
    type: 'CNAME',
    ttl: 3600,
    target: 'sipdir.online.lync.com.',
  });
  pushRecord({
    domain: 'lyncdiscover',
    type: 'CNAME',
    ttl: 3600,
    target: 'webdir.online.lync.com.',
  });
  pushRecord({
    domain: 'msoid',
    type: 'CNAME',
    ttl: 3600,
    target: 'clientconfig.microsoftonline-p.net.',
  });
  pushRecord({
    domain: 'enterpriseregistration',
    type: 'CNAME',
    ttl: 3600,
    target: 'enterpriseregistration.windows.net.',
  });
  pushRecord({
    domain: 'enterpriseenrollment',
    type: 'CNAME',
    ttl: 3600,
    target: 'enterpriseenrollment.manage.microsoft.com.',
  });
  pushRecord({
    domain: '_sip._tls',
    type: 'SRV',
    ttl: 3600,
    target: '100 1 443 sipdir.online.lync.com.',
  });
  pushRecord({
    domain: '_sipfederationtls._tcp',
    type: 'SRV',
    ttl: 3600,
    target: '100 1 5061 sipfed.online.lync.com.',
  });
}

function scarica() {
  let csv = ['DOMINIO', 'TTL', 'TIPO', 'PESO', 'TARGET'];

  csv = [csv.join(';')];

  for (const row of records.value) {
    const csvrow = [];
    for (const f of ['domain', 'ttl', 'type', 'weight', 'target'] as const) {
      let value = row[f];

      if (!isDefined(value)) {
        csvrow.push('');
        continue;
      }

      // Target check
      if (f == 'target' && !isFQ(value ? String(value) : null) && (row.type == 'CNAME' || row.type == 'MX')) {
        value = `${value}.${props.dominio}.`;
      }

      if (typeof value == 'string') {
        value = `"${value.split('"').join('""')}"`;
      } else if (typeof value == 'number') {
        value = value.toLocaleString();
      }

      csvrow.push(value);
    }
    csv.push(csvrow.join(';'));
  }

  const blob = new Blob([csv.join('\r\n')], {
    type: 'text/csv;charset=utf-8',
  });
  saveAs(blob, `${props.dominio.replace('.', '')}-dns.csv`);
}
</script>
